import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { imageUrl } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Upload, Eye } from "lucide-react";

function Field({ label, value, onChange, textarea, rows }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows || 3} className="mt-1 bg-white" />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-white" />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image mise à jour");
    } catch {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex items-center gap-4">
        <img src={imageUrl(value)} alt="" className="h-24 w-32 rounded-lg object-cover ring-1 ring-[#E8D5CE]" />
        <div className="flex-1 space-y-2">
          <Button type="button" variant="outline" onClick={() => ref.current?.click()} data-testid={`upload-${testid}`} className="border-[#D76E4D] text-[#D76E4D]">
            <Upload className="mr-2 h-4 w-4" /> {uploading ? "..." : "Changer l'image"}
          </Button>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ou coller une URL d'image" className="bg-white text-xs" />
        </div>
        <input ref={ref} type="file" accept="image/*" hidden onChange={upload} />
      </div>
    </div>
  );
}

export default function AdminContent() {
  const { refresh } = useContent();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setC(r.data)); }, []);
  if (!c) return <p className="text-[#9B7E73]">Chargement...</p>;

  const setSection = (section, key, val) => setC((prev) => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
  const h = c.histoire_page || {};
  const setH = (key, val) => setSection("histoire_page", key, val);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", c);
      toast.success("Contenu enregistré");
      refresh();
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-serif text-3xl font-medium text-[#2C1A14]">Contenu du site</h1>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-[#D76E4D] hover:underline"><Eye className="h-4 w-4" /> Voir le site</a>
      </div>

      <div className="space-y-6">
        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Bannière d'accueil (Hero)</h2>
          <div className="space-y-4">
            <ImageField label="Image de fond" value={c.hero.image} onChange={(v) => setSection("hero", "image", v)} testid="hero-image" />
            <Field label="Badge (petit texte du haut)" value={c.hero.badge} onChange={(v) => setSection("hero", "badge", v)} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Titre — 1re partie" value={c.hero.title_line1} onChange={(v) => setSection("hero", "title_line1", v)} />
              <Field label="Titre — partie manuscrite" value={c.hero.title_script} onChange={(v) => setSection("hero", "title_script", v)} />
            </div>
            <Field label="Sous-titre" value={c.hero.subtitle} onChange={(v) => setSection("hero", "subtitle", v)} textarea />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Bouton principal" value={c.hero.cta_primary} onChange={(v) => setSection("hero", "cta_primary", v)} />
              <Field label="Bouton secondaire" value={c.hero.cta_secondary} onChange={(v) => setSection("hero", "cta_secondary", v)} />
            </div>
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Section Nouveautés (accueil)</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Sur-titre" value={c.nouveautes.eyebrow} onChange={(v) => setSection("nouveautes", "eyebrow", v)} />
            <Field label="Titre" value={c.nouveautes.title} onChange={(v) => setSection("nouveautes", "title", v)} />
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Encart « Notre histoire » (accueil)</h2>
          <div className="space-y-4">
            <ImageField label="Photo" value={c.story_home.image} onChange={(v) => setSection("story_home", "image", v)} testid="story-image" />
            <Field label="Sur-titre" value={c.story_home.eyebrow} onChange={(v) => setSection("story_home", "eyebrow", v)} />
            <Field label="Titre" value={c.story_home.title} onChange={(v) => setSection("story_home", "title", v)} />
            <Field label="Texte" value={c.story_home.text} onChange={(v) => setSection("story_home", "text", v)} textarea rows={4} />
            <Field label="Bouton" value={c.story_home.button} onChange={(v) => setSection("story_home", "button", v)} />
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Page « Contact »</h2>
          <div className="space-y-4">
            <Field label="Titre" value={c.contact.title} onChange={(v) => setSection("contact", "title", v)} />
            <Field label="Sous-titre" value={c.contact.subtitle} onChange={(v) => setSection("contact", "subtitle", v)} textarea />
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]" data-testid="histoire-editor">
          <h2 className="mb-2 font-serif text-xl text-[#2C1A14]">Page « Notre histoire »</h2>
          <p className="mb-4 text-xs text-[#9B7E73]">Laissez une ligne vide entre deux paragraphes. Entourez un mot de **deux étoiles** pour le mettre en gras.</p>
          <div className="space-y-4">
            <ImageField label="Photo du haut" value={h.hero_image} onChange={(v) => setH("hero_image", v)} testid="histoire-hero-image" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Petit titre manuscrit" value={h.hero_script} onChange={(v) => setH("hero_script", v)} />
              <Field label="Grand titre" value={h.hero_title} onChange={(v) => setH("hero_title", v)} />
            </div>
            <Field label="Sous-titre" value={h.hero_subtitle} onChange={(v) => setH("hero_subtitle", v)} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Sur-titre de l'introduction" value={h.intro_eyebrow} onChange={(v) => setH("intro_eyebrow", v)} />
              <Field label="Titre de l'introduction" value={h.intro_title} onChange={(v) => setH("intro_title", v)} />
            </div>
            <Field label="Texte de l'introduction" value={h.intro_text} onChange={(v) => setH("intro_text", v)} textarea rows={8} />
            <ImageField label="Photo de l'introduction" value={h.intro_image} onChange={(v) => setH("intro_image", v)} testid="histoire-intro-image" />
            <Field label="Texte central" value={h.middle_text} onChange={(v) => setH("middle_text", v)} textarea rows={6} />
            <Field label="Citation (bandeau sombre)" value={h.quote} onChange={(v) => setH("quote", v)} textarea rows={2} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Sur-titre vision" value={h.vision_eyebrow} onChange={(v) => setH("vision_eyebrow", v)} />
              <Field label="Titre vision" value={h.vision_title} onChange={(v) => setH("vision_title", v)} />
            </div>
            <Field label="Texte vision" value={h.vision_text} onChange={(v) => setH("vision_text", v)} textarea rows={6} />
            <Field label="Sur-titre « De son dressing au vôtre »" value={h.dressing_eyebrow} onChange={(v) => setH("dressing_eyebrow", v)} />
            <Field label="Texte « De son dressing au vôtre »" value={h.dressing_text} onChange={(v) => setH("dressing_text", v)} textarea rows={8} />
            <ImageField label="Photo de fin" value={h.end_image} onChange={(v) => setH("end_image", v)} testid="histoire-end-image" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Titre de fin" value={h.end_title} onChange={(v) => setH("end_title", v)} />
              <Field label="Phrase de fin" value={h.end_subtitle} onChange={(v) => setH("end_subtitle", v)} />
            </div>
          </div>
        </section>

        <Button onClick={save} disabled={saving} data-testid="save-content" className="rounded-full bg-[#D76E4D] px-8 py-6 text-white hover:bg-[#C05A3A]">
          {saving ? "Enregistrement..." : "Enregistrer le contenu"}
        </Button>
      </div>
    </div>
  );
}
